import React, {
  forwardRef, useImperativeHandle, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import toastr from 'toastr';
import { post } from '../../helpers/api_helper';

const API = '/api/sourcing/intake/putaway';
const message = (error) => error.response?.data?.error || error.message;
const requestId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(16)))
  .map((n) => n.toString(16).padStart(2, '0')).join('');

const IntakePutawayModal = forwardRef(({
  bins, generation, onClose, onDeleted,
}, ref) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);
  const [scan, setScan] = useState('');
  const [lot, setLot] = useState('');
  const [busy, setBusy] = useState(false);
  const [transfer, setTransfer] = useState(null);
  const [error, setError] = useState('');
  const attempt = useRef(null);
  const rows = useRef(new Map());
  const scanInput = useRef(null);
  const submitting = useRef(false);
  const selectScan = (value) => {
    if (submitting.current || transfer || attempt.current) return;
    const name = value.trim();
    if (!bins.includes(name)) {
      toastr.error(t('Bin {{name}} not found', { name }));
      return;
    }
    setSelected((previous) => (previous.includes(name) ? previous : [...previous, name]));
    rows.current.get(name)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    setScan('');
  };
  useImperativeHandle(ref, () => ({ scan: selectScan }));
  const create = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    // Retain the same body on retry after an uncertain network outcome.
    try {
      if (!attempt.current) {
        attempt.current = {
          generation, bins: selected, lot: lot.trim(), request_id: requestId(),
        };
      }
      setTransfer(await post(API, attempt.current));
    } catch (err) {
      setError(message(err));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  const remove = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      const snapshot = await post(`${API}/delete`, { request_id: transfer.request_id });
      onDeleted(snapshot);
      onClose();
    } catch (err) {
      setError(message(err));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  let actionLabel = 'Create...';
  if (attempt.current) actionLabel = 'Retry';
  if (transfer) actionLabel = 'Delete bins';
  if (busy) actionLabel = 'Please wait…';
  return (
    <Modal isOpen toggle={busy ? undefined : onClose} scrollable backdrop="static" onOpened={() => scanInput.current?.focus()}>
      <ModalHeader toggle={busy ? undefined : onClose}>{t(transfer ? 'Putaway route created' : 'Putaway')}</ModalHeader>
      <ModalBody className="d-flex flex-column" style={{ overflow: 'hidden', minHeight: 0 }}>
        {error && <div role="alert" className="alert alert-danger flex-shrink-0">{error}</div>}
        {transfer ? (
          <p>{t('Route {{id}} was created in Dropzone. Delete the bins sent to this route from Sort?', { id: transfer.id })}</p>
        ) : (
          <>
            <div className="flex-shrink-0">
              <label className="form-label" htmlFor="putaway-lot">{t('Lot')}</label>
              <input id="putaway-lot" className="form-control mb-3" value={lot} maxLength={30} required disabled={busy || !!attempt.current} onChange={(event) => setLot(event.target.value)} />
              <form onSubmit={(event) => { event.preventDefault(); selectScan(scan); }} className="mb-3">
                <label className="form-label" htmlFor="putaway-bin-scan">{t('Scan bin QR code')}</label>
                <div className="d-flex gap-2">
                  <input id="putaway-bin-scan" ref={scanInput} className="form-control" value={scan} onChange={(event) => setScan(event.target.value)} disabled={busy || !!attempt.current} />
                  <button type="submit" className="btn btn-outline-primary" disabled={busy || !!attempt.current}>{t('Select bin')}</button>
                </div>
              </form>
            </div>
            <div style={{ overflowY: 'auto', minHeight: 0 }}>
              {bins.map((bin, index) => (
                <label key={bin} htmlFor={`putaway-bin-${index}`} ref={(node) => { if (node) rows.current.set(bin, node); }} className="d-flex align-items-center gap-2 border-bottom p-3 text-break">
                  <input id={`putaway-bin-${index}`} type="checkbox" checked={selected.includes(bin)} disabled={busy || !!attempt.current} onChange={(event) => setSelected((previous) => (event.target.checked ? [...previous, bin] : previous.filter((name) => name !== bin)))} />
                  {bin}
                </label>
              ))}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>{t(transfer ? 'Keep bins' : 'Cancel')}</button>
        <button type="button" className="btn btn-primary" onClick={transfer ? remove : create} disabled={busy || (!transfer && (!selected.length || !lot.trim()))}>
          {t(actionLabel)}
        </button>
      </ModalFooter>
    </Modal>
  );
});
IntakePutawayModal.displayName = 'IntakePutawayModal';
IntakePutawayModal.propTypes = {
  bins: PropTypes.arrayOf(PropTypes.string).isRequired,
  generation: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
};
export default IntakePutawayModal;
